import { useMemo } from "react";
import { useForm } from "react-hook-form";
import type { Error as SignupError } from "@auth0/auth0-acul-js";

import { butterfieldLogo } from "@/assets/logos";
import GoogleLogo from "@/components/GoogleLogo";
import { useSignupIdManager } from "../hooks/useSignupIdManager";
import { getFieldError } from "../utils/helpers/errorUtils";
import { buildIdentifierFields } from "../utils/helpers/identifierFields";
import { rebaseLinkToCurrentOrigin } from "../utils/helpers/urlUtils";

// const heroImageUrl =
//   "https://media.butterfield.com/page-image_imageUrl_a470c5db-2106-4964-a8f2-414c0415efe6_1744290400564.jpg";
const heroImageUrl =
  "https://cdn.mediavalet.com/cato/butterfield/aEeF6_P6lk6o6VqdMg_3cQ/GX_bog2pTkawcjy_quukQA/Large/DSC09355-slovenia-ivan-sardi-2026.jpeg?f=auto";

interface SignUpIdFormData {
  email?: string;
  username?: string;
  phoneNumber?: string;
  captcha?: string;
}

function SignUpIdScreen() {
  const {
    handleSignupId,
    handleFederatedSignup,
    errors,
    isCaptchaAvailable,
    captchaImage,
    texts,
    requiredIdentifiers,
    optionalIdentifiers,
    usernamePolicy,
    alternateConnections,
    loginLink,
    submittedFormData,
  } = useSignupIdManager();

  const identifierFields = useMemo(
    () => buildIdentifierFields(requiredIdentifiers, optionalIdentifiers, texts),
    [requiredIdentifiers, optionalIdentifiers, texts]
  );

  const form = useForm<SignUpIdFormData>({
    defaultValues: {
      email: submittedFormData?.email || "",
      username: submittedFormData?.username || "",
      phoneNumber: submittedFormData?.phone || "",
      captcha: "",
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
  } = form;

  const sdkErrors = (errors || []) as SignupError[];
  const generalErrors = sdkErrors.filter((error) => !error.field);

  const captchaSDKError = getFieldError("captcha", sdkErrors);
  const buttonText = texts?.buttonText || "Continue";
  const captchaLabel = (texts?.captchaCodePlaceholder || "CAPTCHA").concat("*");
  const headingText =
    texts?.title || texts?.heading || "Join Butterfield & Robinson";
  const descriptionText = texts?.description;

  const loginActionText =
    texts?.loginActionText || texts?.loginActionCta || "Already have an account?";
  const loginLinkText = texts?.loginActionLinkText || "Sign in";
  const localizedLoginLink =
    loginLink && rebaseLinkToCurrentOrigin(loginLink as string);

  type ConnectionLike = {
    name?: string;
    options?: { displayName?: string };
    metadata?: { displayName?: string };
  };

  const getConnectionLabel = (connection: ConnectionLike) =>
    connection.options?.displayName ||
    connection.metadata?.displayName ||
    connection.name;

  const isGoogleConnection = (connection: ConnectionLike) => {
    const connectionName = connection.name?.toLowerCase();
    const connectionLabel = getConnectionLabel(connection)?.toLowerCase();
    return (
      connectionName === "google-oauth2" ||
      (connectionLabel ? connectionLabel.includes("google") : false)
    );
  };

  const isLinkedInConnection = (connection: ConnectionLike) => {
    const connectionName = connection.name?.toLowerCase();
    const connectionLabel = getConnectionLabel(connection)?.toLowerCase();
    return (
      connectionName === "linkedin" ||
      connectionName === "linkedin-oauth2" ||
      (connectionLabel ? connectionLabel.includes("linkedin") : false)
    );
  };

  const visibleConnections = alternateConnections.filter(
    (connection) => !isLinkedInConnection(connection as ConnectionLike)
  );

  const onSubmit = async (data: SignUpIdFormData) => {
    await handleSignupId({
      email: data.email?.trim() || undefined,
      username: data.username?.trim() || undefined,
      phone: data.phoneNumber?.trim() || undefined,
      captcha: data.captcha?.trim() || undefined,
    });
  };

  return (
    <div className="overflow-hidden rounded-none bg-white shadow-[0_30px_80px_rgba(2,6,23,0.35)] min-h-screen">
      <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
        <div className="relative order-1 h-56 sm:h-72 md:order-2 md:h-full">
          <img
            src={heroImageUrl}
            alt="Auth0 experience preview"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/10 via-transparent to-slate-900/30" />
        </div>

        <form
          className="flex h-full flex-col justify-center gap-6 order-2 bg-white px-10 py-12 md:order-1 md:px-12"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-0 flex flex-col items-start text-left">
            <img
              src={butterfieldLogo}
              alt="Butterfield logo"
              className="h-14 w-auto"
              loading="lazy"
            />
            <p className="mt-4 font-display text-xl font-normal text-ink">
              {headingText}
            </p>
            {descriptionText && (
              <p className="mt-2 text-sm text-ink-2">{descriptionText}</p>
            )}
          </div>

          {generalErrors.length > 0 && (
            <div className="rounded-none border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
              <ul className="list-disc space-y-1 pl-5">
                {generalErrors.map((error, index) => (
                  <li key={`signupid-general-error-${index}`}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {identifierFields.map((field) => {
            const sdkError =
              field.name === "phoneNumber"
                ? getFieldError("phone_number", sdkErrors) ||
                  getFieldError("phone", sdkErrors)
                : getFieldError(field.name as string, sdkErrors);

            const validationRules =
              field.name === "username"
                ? {
                    required: field.required ? "This field is required" : undefined,
                    minLength: usernamePolicy?.minLength
                      ? {
                          value: usernamePolicy.minLength,
                          message: `Username must be at least ${usernamePolicy.minLength} characters`,
                        }
                      : undefined,
                    maxLength: usernamePolicy?.maxLength
                      ? {
                          value: usernamePolicy.maxLength,
                          message: `Username must be at most ${usernamePolicy.maxLength} characters`,
                        }
                      : {
                          value: 100,
                          message: "Maximum 100 characters allowed",
                        },
                  }
                : field.name === "phoneNumber"
                ? {
                    required: field.required ? "This field is required" : undefined,
                    maxLength: {
                      value: 30,
                      message: "Maximum 30 characters allowed",
                    },
                  }
                : {
                    required: field.required ? "This field is required" : undefined,
                    maxLength:
                      field.name === "email"
                        ? {
                            value: 128,
                            message: "Maximum 128 characters allowed",
                          }
                        : undefined,
                  };

            return (
              <div key={field.name} className="space-y-2">
                <label
                  className="text-sm font-semibold text-ink-2"
                  htmlFor={field.name}
                >
                  {field.label}
                </label>
                <input
                  id={field.name}
                  type={field.inputType}
                  autoComplete={field.autoComplete}
                  placeholder={field.placeholder}
                  className="input-field"
                  aria-invalid={
                    !!formErrors[field.name as keyof SignUpIdFormData]?.message ||
                    !!sdkError ||
                    undefined
                  }
                  {...register(field.name as keyof SignUpIdFormData, validationRules)}
                />
                {(formErrors[field.name as keyof SignUpIdFormData]?.message ||
                  sdkError) && (
                  <p className="text-sm text-danger">
                    {formErrors[field.name as keyof SignUpIdFormData]?.message ||
                      sdkError}
                  </p>
                )}
              </div>
            );
          })}

          {isCaptchaAvailable && (
            <div className="space-y-3">
              <label
                htmlFor="captcha"
                className="text-sm font-semibold text-ink-2"
              >
                {captchaLabel}
              </label>
              {captchaImage && (
                <img
                  src={captchaImage}
                  alt="CAPTCHA challenge"
                  className="h-16 w-full rounded-none border border-border object-contain"
                />
              )}
              <input
                id="captcha"
                type="text"
                placeholder={texts?.captchaCodePlaceholder || "Enter the code"}
                className="input-field"
                aria-invalid={
                  !!formErrors.captcha?.message || !!captchaSDKError || undefined
                }
                {...register("captcha", {
                  required: "Please complete the CAPTCHA",
                  maxLength: {
                    value: 15,
                    message: "CAPTCHA code is too long",
                  },
                })}
              />
              {(formErrors.captcha?.message || captchaSDKError) && (
                <p className="text-sm text-danger">
                  {formErrors.captcha?.message || captchaSDKError}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-sm border border-accent bg-accent px-4 py-3 text-base font-semibold uppercase tracking-[0.1em] text-on-accent transition hover:cursor-pointer hover:border-accent-hover hover:bg-accent-hover hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Please wait..." : buttonText}
          </button>

          <p className="text-sm text-ink-2">
            Problems signing in?{" "}
            <a
              href="https://www.butterfield.com/my-account-faq"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ink-2 underline underline-offset-2 hover:cursor-pointer hover:text-ink"
            >
              Click here
            </a>
            .
          </p>

          {visibleConnections.length > 0 && (
            <>
              <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-ink-3">
                <span className="h-px flex-1 bg-border" aria-hidden />
                <span>{texts?.separatorText || "or"}</span>
                <span className="h-px flex-1 bg-border" aria-hidden />
              </div>

              <div className="space-y-3">
                {visibleConnections.map((connection) => {
                  const enhancedConnection = connection as ConnectionLike;

                  const connectionLabel = getConnectionLabel(enhancedConnection);
                  const connectionCta = isGoogleConnection(enhancedConnection)
                    ? "Continue with Google"
                    : connectionLabel;
                  const showGoogleLogo = isGoogleConnection(enhancedConnection);

                  return (
                    <button
                      key={enhancedConnection.name}
                      type="button"
                      onClick={() =>
                        enhancedConnection.name &&
                        handleFederatedSignup(enhancedConnection.name)
                      }
                      className="flex w-full items-center justify-center gap-3 rounded-sm border border-border bg-white px-4 py-3 text-base font-semibold text-ink transition hover:cursor-pointer hover:bg-btn-hover hover:shadow-sm"
                    >
                      {showGoogleLogo && (
                        <span className="inline-flex items-center justify-center rounded-full bg-white">
                          <GoogleLogo className="h-5 w-5" />
                        </span>
                      )}
                      {connectionCta}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <p className="text-sm text-ink-2">
            {loginActionText}{" "}
            {localizedLoginLink ? (
              <a
                href={localizedLoginLink}
                className="font-semibold text-ink underline underline-offset-2"
              >
                {loginLinkText}
              </a>
            ) : (
              <span className="font-semibold text-ink">{loginLinkText}</span>
            )}
            .
          </p>
        </form>
      </div>
    </div>
  );
}

export default SignUpIdScreen;
